import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import {
  IoCheckmarkCircle,
  IoCloseCircle,
  IoWarning,
  IoInformationCircle,
  IoClose,
} from 'react-icons/io5';
import { ToastType, ToastProps } from '../../contexts/ToastContext';

const Toast: React.FC<ToastProps> = ({ id, type, title, message, duration = 5000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }

    return () => {};
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.(id);
    }, 300);
  };

  if (!isVisible) return null;

  const icons = {
    success: <IoCheckmarkCircle className="w-5 h-5" />,
    error: <IoCloseCircle className="w-5 h-5" />,
    warning: <IoWarning className="w-5 h-5" />,
    info: <IoInformationCircle className="w-5 h-5" />,
  };

  const styles = {
    success: {
      container: 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800',
      icon: 'text-green-500 dark:text-green-400',
      title: 'text-green-900 dark:text-green-100',
      message: 'text-green-700 dark:text-green-200',
    },
    error: {
      container: 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800',
      icon: 'text-red-500 dark:text-red-400',
      title: 'text-red-900 dark:text-red-100',
      message: 'text-red-700 dark:text-red-200',
    },
    warning: {
      container: 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800',
      icon: 'text-amber-500 dark:text-amber-400',
      title: 'text-amber-900 dark:text-amber-100',
      message: 'text-amber-700 dark:text-amber-200',
    },
    info: {
      container: 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800',
      icon: 'text-blue-500 dark:text-blue-400',
      title: 'text-blue-900 dark:text-blue-100',
      message: 'text-blue-700 dark:text-blue-200',
    },
  };

  const style = styles[type];

  return (
    <div
      className={cn(
        'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border shadow-lg transition-all duration-300',
        style.container,
        isLeaving
          ? 'animate-out fade-out slide-out-to-right'
          : 'animate-in fade-in slide-in-from-right'
      )}
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className={cn('flex-shrink-0', style.icon)}>{icons[type]}</div>
          <div className="ml-3 w-0 flex-1">
            {title && <p className={cn('text-sm font-medium', style.title)}>{title}</p>}
            <p className={cn('text-sm', title ? 'mt-1' : '', style.message)}>{message}</p>
          </div>
          <div className="ml-4 flex flex-shrink-0">
            <button
              className={cn(
                'inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
                'transition-colors duration-200'
              )}
              onClick={handleClose}
            >
              <span className="sr-only">Close</span>
              <IoClose className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export new ToastContainer that uses global context
export { ToastContainer } from './ToastContainer';

// Export types and context hook for global toast system
export { useToastContext as useToast } from '../../contexts/ToastContext';
export type { ToastProps, ToastType } from '../../contexts/ToastContext';

// Helper functions for common toast types
export const toast = {
  success: (message: string, title?: string) => ({
    type: 'success' as ToastType,
    title: title || 'Success',
    message,
  }),
  error: (message: string, title?: string) => ({
    type: 'error' as ToastType,
    title: title || 'Error',
    message,
  }),
  warning: (message: string, title?: string) => ({
    type: 'warning' as ToastType,
    title: title || 'Warning',
    message,
  }),
  info: (message: string, title?: string) => ({
    type: 'info' as ToastType,
    title: title || 'Info',
    message,
  }),
};

export default Toast;
